// SYSTEM_ROUTE: /knowledge/graph
import type { KnowledgeGraph, Relationship } from '@life-os/app-core';
import { edgeLabelFrom } from '@life-os/app-core';
import { Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

export function KnowledgeGraphScreen() {
  const services = useServices();
  const { revision, data } = useStore();
  const graph = useAsync<KnowledgeGraph>(() => services.notes.graph(), [revision]);
  const links = useAsync<readonly Relationship[]>(() => data.graph.all(), [revision]);

  const g = graph.value;
  const nodes = g ? [...g.nodes].sort((a, b) => b.backlinks - a.backlinks) : [];
  const titleById = new Map(nodes.map((n) => [String(n.id), n.title]));

  // Group outgoing edges by source for a readable adjacency view.
  const outgoing = new Map<string, string[]>();
  for (const e of g?.edges ?? []) {
    const list = outgoing.get(String(e.fromId)) ?? [];
    list.push(titleById.get(String(e.toId)) ?? '(unknown)');
    outgoing.set(String(e.fromId), list);
  }

  const orphans = nodes.filter((n) => n.backlinks === 0 && (outgoing.get(String(n.id)) ?? []).length === 0);

  const rels = links.value ?? [];

  return (
    <>
      <ScreenHeader title={<>Knowledge Graph</>} subtitle={<>How your notes connect. Edges come from [[wiki links]]; backlinks show what points back. This
            is a live view of your notes, never a separate copy.</>} actions={<>{g ? (
          <div className="lc-row__meta">
            <Chip tone="neutral">{g.nodes.length} notes</Chip>
            <Chip tone="primary">{g.edges.length} links</Chip>
          </div>
        ) : null}</>} />

      <Pane
        title="Cross-domain links"
        hint="Typed relationships across systems — one object, many lenses"
      >
        {rels.length === 0 ? (
          <EmptyState title="No cross-domain links yet">
            Link a task to a project, a problem to a goal, or a habit to an outcome. They will appear here
            as typed relationships, and as backlinks on each entity.
          </EmptyState>
        ) : (
          <Stack>
            {rels.map((r) => (
              <CompactRow
                key={r.id}
                title={`${r.from.kind.replace(/_/g, ' ')} → ${r.to.kind.replace(/_/g, ' ')}`}
                subtitle={r.note ?? `${r.from.kind.replace(/_/g, ' ')} ${edgeLabelFrom(r.from, r)} this ${r.to.kind.replace(/_/g, ' ')}`}
                meta={<Chip tone="primary">{r.kind.replace(/_/g, ' ')}</Chip>}
              />
            ))}
          </Stack>
        )}
      </Pane>

      {nodes.length === 0 ? (
        <Pane title="Graph">
          <EmptyState title="No notes to graph yet">
            Create notes and connect them with [[Title]] links. They will appear here as a connected web.
          </EmptyState>
        </Pane>
      ) : (
        <>
          <Pane title="Most linked" hint="Notes others point to most">
            <Stack>
              {nodes.slice(0, 8).map((n) => (
                <CompactRow
                  key={String(n.id)}
                  title={n.title}
                  subtitle={(outgoing.get(String(n.id)) ?? []).length > 0 ? `→ ${(outgoing.get(String(n.id)) ?? []).join(', ')}` : 'no outgoing links'}
                  meta={<Chip tone={n.backlinks > 0 ? 'primary' : 'muted'}>{n.backlinks} backlink{n.backlinks === 1 ? '' : 's'}</Chip>}
                />
              ))}
            </Stack>
          </Pane>

          {orphans.length > 0 ? (
            <Pane title="Unconnected notes" hint="Candidates to link in">
              <Stack>
                {orphans.map((n) => (
                  <CompactRow key={String(n.id)} title={n.title} meta={<Chip tone="muted">orphan</Chip>} />
                ))}
              </Stack>
            </Pane>
          ) : null}
        </>
      )}
    </>
  );
}
